/**
 * `animationClips.ts` — the one definition of a model's animation TAKES.
 *
 * Three consumers need the same answer and would otherwise each derive it:
 * the agent tool (`Entities_Executor.toggleModelAnimation`, resolving a take
 * the user named), the scene digest (`AIContextEngineModel`, listing what a
 * model has), and the inspector (`useAnimation`, whose Take dropdown was
 * declared and never filled). Two derivations of "which clip is index 2" is
 * how the three come to disagree.
 *
 * ⚠ CLIP ORDER IS ALPHABETICAL BY NAME, NOT glTF DECLARATION ORDER, and that
 * is a CROSS-CLIENT CONTRACT rather than a preference. `AnimationClipIndex` is
 * a shared Dynamo attribute both clients write, and the desktop client indexes
 * `AnimationExt.GetOrderedClips` — `animation.OfType<AnimationState>()
 * .OrderBy(s => s.name).ToArray()` (`AnimationController.cs:480-483`) — from
 * `ApplySettings` (:136), `ReadSettings` (:106) and `GetClips` (:296) alike.
 * Indexing three's `GLTF.animations`, which is declaration order, therefore
 * selects a DIFFERENT clip than desktop for the same persisted value on any
 * model whose clips are not declared alphabetically. Nothing errors; the wrong
 * animation simply plays. Both sorts are stable (ES2019 `Array.sort`,
 * LINQ `OrderBy`), so duplicate names keep declaration order on both sides.
 *
 * A BLANK name becomes the positional literal `Take {i}` — verbatim
 * `SelectedGameObjectModel.cs:929`, and computed AFTER ordering so the number
 * is the take's index, which is what the user is shown and what is persisted.
 */
import { Ioc } from "@core/ioc/ioc";
import { GLTF_ASSET_CACHE, type GltfAssetCache } from "./gltfAssetCache";

/** Fixed locale: the order must not vary with the viewer's browser. */
const K_CLIP_COLLATOR = new Intl.Collator("en");

/** The label a nameless clip is shown under. Unity: `$"Take {i}"`. */
export function animationTakeLabel(name: string, index: number): string {
  return name.trim().length > 0 ? name : `Take ${index}`;
}

/**
 * Unity's `GetOrderedClips` order — alphabetical by name, stable.
 *
 * Generic over anything carrying a `name` so the renderer can order its
 * NORMALIZED clip objects and the cache path can order the raw ones through
 * the same comparator; ordering the two differently is the bug this exists to
 * prevent.
 */
export function orderAnimationClips<T extends { readonly name: string }>(
  clips: ReadonlyArray<T>,
): ReadonlyArray<T> {
  // ⚠ LINGUISTIC, NOT ORDINAL. `OrderBy(s => s.name)` passes no comparer, so
  // it gets `Comparer<string>.Default` → `String.CompareTo` → a LINGUISTIC
  // comparison, and the app pins `CultureInfo.InvariantCulture`
  // (`AppConfig.cs:422`) so upstream's order is stable rather than per-user.
  // Measured under Mono and .NET against this exact data: `["Walk","attack"]`
  // orders `attack, Walk` there and `Walk, attack` under `<`/`>`, so an
  // ordinal sort disagrees with the desktop client on any mixed-case set —
  // which is most of them. A collator PINNED to one locale is deterministic
  // across browsers AND matches that ordering.
  return [...clips].sort((a, b) => K_CLIP_COLLATOR.compare(a.name, b.name));
}

/** Ordered take labels for a clip list, with the `Take {i}` fallback applied. */
export function animationTakeLabels(
  clips: ReadonlyArray<{ readonly name: string }>,
): ReadonlyArray<string> {
  return orderAnimationClips(clips).map((clip, index) => animationTakeLabel(clip.name, index));
}

/**
 * Ordered take labels for an asset, or `null` for UNKNOWN.
 *
 * `null` is a FOUR-WAY ambiguity — no asset id, no viewport has mounted the
 * cache, the asset has not been decoded yet, or it is a format
 * `GltfAssetCache` skips — and callers must never read it as "no takes".
 * A decoded asset with zero clips answers `[]`, which is a real fact and a
 * different one. This is the same contract `animationClipCount` carried, and
 * it is why the digest prints nothing for `null` and a real `(no animation)`
 * for `[]`.
 */
export function getAssetAnimationTakes(
  assetId: string | null,
): ReadonlyArray<string> | null {
  if (!assetId || !Ioc.isRegistered<GltfAssetCache>(GLTF_ASSET_CACHE)) return null;
  const asset = Ioc.resolve<GltfAssetCache>(GLTF_ASSET_CACHE).get(assetId);
  return asset ? animationTakeLabels(asset.gltf.animations) : null;
}

/**
 * Clip count for an asset, or `null` for UNKNOWN — see
 * {@link getAssetAnimationTakes} for what `null` does and does not mean.
 */
/**
 * How many assets have been decoded so far — the cheapest signal that a
 * takes-derived answer may have changed. `0` when no cache is mounted, which
 * is also a stable value, so a consumer keyed on it re-derives once the cache
 * appears.
 */
export function animationDecodeGeneration(): number {
  if (!Ioc.isRegistered<GltfAssetCache>(GLTF_ASSET_CACHE)) return 0;
  return Ioc.resolve<GltfAssetCache>(GLTF_ASSET_CACHE).decodeGeneration;
}

export function animationClipCount(assetId: string | null): number | null {
  return getAssetAnimationTakes(assetId)?.length ?? null;
}

/**
 * Resolve what a caller CALLED a take into its persisted index.
 *
 * The agent and a workflow author name a take (`"Walk"`); the wire and the
 * desktop client store an index. Accepted, in order: a plain integer already
 * in range; an exact case-insensitive name; a unique case-insensitive prefix.
 *
 * A prefix that matches SEVERAL takes is refused rather than resolved to the
 * first — picking one would silently play a different animation than the one
 * asked for, and the caller can disambiguate from the list in the error.
 */
export type AnimationTakeResolution =
  | { readonly ok: true; readonly index: number; readonly label: string }
  | { readonly ok: false; readonly reason: string };

export function resolveAnimationTake(
  takes: ReadonlyArray<string>,
  query: string,
): AnimationTakeResolution {
  const wanted = query.trim();
  if (wanted.length === 0) return { ok: false, reason: "take is empty" };
  if (takes.length === 0) return { ok: false, reason: "model has no animation takes" };

  // ⚠ NAME BEFORE INDEX. A clip may legitimately be CALLED "1" — and a
  // nameless one is labelled `Take {i}`, so digits in a take name are not
  // exotic. Testing the integer form first resolved "1" to position 1 rather
  // than to the clip the user could see named "1", silently playing a
  // different animation.
  const lowered = wanted.toLowerCase();
  const exact = takes.findIndex((take) => take.toLowerCase() === lowered);
  if (exact >= 0) return { ok: true, index: exact, label: takes[exact]! };

  // A bare integer is the persisted form; accept it so a round-tripped value
  // and an agent that read an index both work.
  if (/^\d+$/.test(wanted)) {
    const index = Number.parseInt(wanted, 10);
    if (index >= 0 && index < takes.length) {
      return { ok: true, index, label: takes[index]! };
    }
    return {
      ok: false,
      reason: `take index ${index} is out of range (model has ${takes.length})`,
    };
  }

  const prefixes: number[] = [];
  takes.forEach((take, index) => {
    if (take.toLowerCase().startsWith(lowered)) prefixes.push(index);
  });
  if (prefixes.length === 1) {
    const index = prefixes[0]!;
    return { ok: true, index, label: takes[index]! };
  }
  if (prefixes.length > 1) {
    return {
      ok: false,
      reason:
        `take '${wanted}' matched ${prefixes.length} takes `
        + `(${prefixes.map((i) => `'${takes[i]}'`).join(", ")})`,
    };
  }
  return {
    ok: false,
    reason: `no take named '${wanted}' (model has ${takes.map((t) => `'${t}'`).join(", ")})`,
  };
}
